import {
  selectAppStateSelectedCustomer,
  selectedCustomer,
} from '@app/ngrx-customer/store';
import { createSelector } from '@ngrx/store';
import { selectOnline } from '@panamax/app-state';
import { ImageVariantEnum } from '@product-detail/models/image-variant.enum';
import {
  productDetailViewModelSelector,
  productDetailsViewModelSelector,
} from '@product-detail/selectors/product-detail.selector';
import { getFirstImageURL } from '@product-detail/utils/product-images.util';
import {
  acceptedCount,
  allBetterBuys,
  remainingCount,
  getBetterBuysById,
  BetterBuysState,
  selectProductSummaryEntities,
} from '@usf/ngrx-product';
import {
  BetterBuyProductDetail,
  BetterBuysPageViewModel,
  BetterBuysPageViewModelV2,
  BetterBuysWithDetails,
} from '../models/better-buys.model';
import {
  IListState,
  ListItemState,
  ListState,
  listAdapter,
  listItemAdapter,
  selectListItemsState,
  selectListsState,
} from '@usf/ngrx-list';
import { Dictionary } from '@ngrx/entity';
import { createListKey } from '../../lists/shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { ListItem } from '@usf/list-types';
import { ProductDetailViewModel } from '../../product-detail/models/product-detail-view-model';
import { ProductConversionContainer } from '../../shared/models/product-conversion.model';
import { selectProductPricingEntities } from '@usf/ngrx-pricing';
import { ProductPricing } from '@usf/price-types';
import { ProductSummaryState } from '@usf/ngrx-product/lib/models/product-summary-state.model';
import { getAnnualSavings } from '../utils/better-buys-calculation.util';
import { ProductPriceUtil } from '../../product-detail/utils/product-price.util';
import { Customer } from '@usf/customer-types';

// TODO Remove
export const selectBetterBuysWithDetails = (productNumbers: number[]) =>
  createSelector(
    allBetterBuys(),
    productDetailsViewModelSelector(productNumbers),
    selectAppStateSelectedCustomer,
    selectOnline(),
    remainingCount,
    acceptedCount,
    (betterBuys, details, selectedCustomer, isOnline, remaining, accepted) => {
      const betterBuysWithDetails: BetterBuysWithDetails[] = [];
      betterBuys.forEach(betterBuy => {
        const originalProductDetails = details.get(
          betterBuy.originalProductNumber,
        );
        const betterBuyProductDetails = details.get(
          betterBuy.betterBuyProductNumber,
        );
        const originalProduct: BetterBuyProductDetail = {
          ...originalProductDetails,
          smallImageUrl: getFirstImageURL(
            originalProductDetails?.summary,
            ImageVariantEnum.Small,
          ),
          benefits: formatBenefits(originalProductDetails?.detail?.benefits),
        };
        const betterBuyProduct: BetterBuyProductDetail = {
          ...betterBuyProductDetails,
          smallImageUrl: getFirstImageURL(
            betterBuyProductDetails?.summary,
            ImageVariantEnum.Small,
          ),
          benefits: formatBenefits(betterBuyProductDetails?.detail?.benefits),
        };
        const betterBuyWithDetails: BetterBuysWithDetails = {
          ...betterBuy,
          originalProduct,
          betterBuyProduct,
        };
        betterBuysWithDetails.push(betterBuyWithDetails);
      });
      const betterBuysPageViewModel: BetterBuysPageViewModel = {
        betterBuysWithDetails,
        pricingDisabled: selectedCustomer?.ogPrintPriceInd === 'N',
        isOnline,
        remaining,
        accepted,
      };
      return betterBuysPageViewModel;
    },
  );

export const selectBetterBuysViewModelV2 = (productNumbers: number[]) =>
  createSelector(
    selectProductConversionContainerForBetterBuys(productNumbers),
    selectAppStateSelectedCustomer,
    selectOnline(),
    remainingCount,
    acceptedCount,
    (
      productConversionContainers,
      selectedCustomer,
      isOnline,
      remaining,
      accepted,
    ) => {
      const betterBuysPageViewModel: BetterBuysPageViewModelV2 = {
        productConversionContainers,
        pricingDisabled: selectedCustomer?.ogPrintPriceInd === 'N',
        isOnline,
        remaining,
        accepted,
      };
      return betterBuysPageViewModel;
    },
  );

//This selector does all the savings calculations and returns a dictionary of BetterBuysState with key as originalProductNumber
export const selectBetterBuysWithSavingData = createSelector(
  allBetterBuys(),
  selectProductPricingEntities,
  selectProductSummaryEntities,
  selectedCustomer,
  (
    betterBuys: BetterBuysState[],
    pricing: Dictionary<ProductPricing>,
    productSummaryEntities: Dictionary<ProductSummaryState>,
    selectedCustomer: Customer,
  ) => {
    const betterBuysWithSavingsData: Dictionary<BetterBuysState> = {};
    betterBuys.forEach(betterBuy => {
      const originalProductSummary =
        productSummaryEntities[betterBuy.originalProductNumber];
      const betterBuyProductSummary =
        productSummaryEntities[betterBuy.betterBuyProductNumber];
      const originalProductPricing = pricing[betterBuy.originalProductNumber];
      const betterBuyProductPricing = pricing[betterBuy.betterBuyProductNumber];

      let originalProductPricePerPortion = 0;
      let betterBuyProductPricePerPortion = 0;

      //Calculating and populating  price per portion  fields in better Buy state  if packsizes are different
      if (
        originalProductSummary?.salesPackSize !=
          betterBuyProductSummary?.salesPackSize &&
        originalProductPricing?.priceUom ===
          betterBuyProductPricing?.priceUom &&
        originalProductSummary?.standardComparisonUOM &&
        betterBuyProductSummary?.standardComparisonUOM &&
        originalProductSummary?.standardComparisonValue &&
        betterBuyProductSummary?.standardComparisonValue
      ) {
        originalProductPricePerPortion =
          ProductPriceUtil.calculatePricePerPortion(
            originalProductSummary,
            selectedCustomer,
            originalProductPricing,
            originalProductSummary?.standardComparisonUOM,
            originalProductSummary?.standardComparisonValue,
          );
        betterBuyProductPricePerPortion =
          ProductPriceUtil.calculatePricePerPortion(
            betterBuyProductSummary,
            selectedCustomer,
            betterBuyProductPricing,
            betterBuyProductSummary?.standardComparisonUOM,
            betterBuyProductSummary?.standardComparisonValue,
          );
      }
      let BBwithPricePerPortion = {
        ...betterBuy,
      };
      if (
        originalProductPricePerPortion &&
        betterBuyProductPricePerPortion &&
        originalProductPricePerPortion > 0 &&
        betterBuyProductPricePerPortion > 0
      ) {
        BBwithPricePerPortion = {
          ...BBwithPricePerPortion,
          originalProductPricePerPortion: originalProductPricePerPortion,
          betterBuyProductPricePerPortion: betterBuyProductPricePerPortion,
        };
      }

      const updatedBetterBuy = getAnnualSavings(
        originalProductSummary,
        betterBuyProductSummary,
        originalProductPricing,
        betterBuyProductPricing,
        BBwithPricePerPortion,
      );
      betterBuysWithSavingsData[betterBuy.originalProductNumber] =
        updatedBetterBuy;
    });
    return betterBuysWithSavingsData;
  },
);

// Calculate total annual savings for home page tile and page header
export const calculateTotalAnnualSavings = createSelector(
  selectBetterBuysWithSavingData,
  (betterBuysWithSavingData: Dictionary<BetterBuysState>) => {
    let totalAnnualSavings = 0;
    for (const [originalProductNumber, betterBuy] of Object.entries(
      betterBuysWithSavingData,
    )) {
      totalAnnualSavings += betterBuy.annualSavings;
    }

    return totalAnnualSavings;
  },
);

export const selectProductConversionContainerForBetterBuys = (
  productNumbers: number[],
) =>
  createSelector(
    selectBetterBuysWithSavingData,
    productDetailsViewModelSelector(productNumbers),
    (
      betterBuysWithSavingData: Dictionary<BetterBuysState>,
      products: Map<number, ProductDetailViewModel>,
    ) => {
      const productConversionContainers: ProductConversionContainer[] = [];
      for (const [originalProductNumber, betterBuy] of Object.entries(
        betterBuysWithSavingData,
      )) {
        const originalProduct = products.get(betterBuy.originalProductNumber);
        const replacementProduct = products.get(
          betterBuy.betterBuyProductNumber,
        );

        // Needed for list updates, can be empty for better buys since this value means nothing for BB.
        const listsThatAreUpdated = [];
        const productConversionContainer: ProductConversionContainer = {
          originalProduct: {
            ...originalProduct,
            smallImageUrl: getFirstImageURL(
              originalProduct?.summary,
              ImageVariantEnum.Small,
            ),
            listsThatAreUpdated,
          },
          replacementProduct: {
            ...replacementProduct,
            smallImageUrl: getFirstImageURL(
              replacementProduct?.summary,
              ImageVariantEnum.Small,
            ),
            formattedBenefits: formatBenefits(
              replacementProduct?.detail?.benefits,
            ),
            savings: betterBuy.savings,
            savingsUom: betterBuy.savingsUom
              ? betterBuy.savingsUom.toUpperCase()
              : null,
            annualSavings: betterBuy.annualSavings,
            annualSavingsUom: betterBuy.annualSavingsUom,
            savingsPerCase:
              betterBuy.savingsPerCase != betterBuy.savings
                ? betterBuy.savingsPerCase
                : 0,
            samePackSize:
              replacementProduct?.summary?.salesPackSize ===
              originalProduct?.summary?.salesPackSize,
            reasonCode: betterBuy.reason,
            pricingAvailableForSavings:
              (replacementProduct?.pricing?.eachPrice > 0 ||
                replacementProduct?.pricing?.unitPrice > 0) &&
              (originalProduct?.pricing?.eachPrice > 0 ||
                originalProduct?.pricing?.unitPrice > 0),
            listsThatAreUpdated,
          },
        };
        productConversionContainers.push(productConversionContainer);
      }

      return productConversionContainers;
    },
  );

export const selectBetterBuyDetailsById = (betterBuyProductNumber: number) =>
  createSelector(
    productDetailViewModelSelector(betterBuyProductNumber),
    betterBuyProductDetails => {
      const betterBuyProduct: BetterBuyProductDetail = {
        ...betterBuyProductDetails,
        smallImageUrl: getFirstImageURL(
          betterBuyProductDetails?.summary,
          ImageVariantEnum.Small,
        ),
        benefits: formatBenefits(betterBuyProductDetails?.detail?.benefits),
      };
      return betterBuyProduct;
    },
  );

export const selectBetterBuyListItems = (productNumber: number) =>
  createSelector(
    listAdapter.getSelectors(selectListsState).selectEntities,
    listItemAdapter.getSelectors(selectListItemsState).selectEntities,
    (lists: Dictionary<ListState>, listItems: Dictionary<ListItemState>) => {
      const itemsToSwap: ListItem[] = [];
      for (const listItemKey in listItems) {
        if (listItemKey && listItems[listItemKey] !== undefined) {
          const listItem = listItems[listItemKey];
          const hasSameProductNumber = listItem.productNumber === productNumber;
          const listKey = createListKey(listItem.listKey);
          const list = lists[listKey];
          const isOnPublicList = list?.listState === 'PUBLIC';
          if (hasSameProductNumber && isOnPublicList) {
            itemsToSwap.push(listItem);
          }
        }
      }
      return itemsToSwap;
    },
  );

export const formatBenefits = (benefits: string) => {
  if (benefits) {
    const substrings = benefits
      ? benefits.split(/~/).filter(a => a.trim() !== '')
      : null;
    return substrings.slice(0, 3);
  }
};
